import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { cloneDeep, isEmpty } from 'lodash';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { toastApiError } from '../../utils/ApiError';
import { getInstitutionTemplate } from '../../utils/Institution';
import { Institution } from '../../interfaces/Institution';
import { upsertInstitution } from '../../services/Institution';
import Waiting from '../Waiting';
import { sharedColors, sharedStyles } from '../../utils/Style';
import CityDropdown from '../City/CityDropdown';
import NumberInput from '../NumberInput';

interface UpsertInstitutionDialogProps {
  open: boolean;
  clickedInstitution?: Institution;
  onClose: () => void;
  onModify: () => void;
}

const UpsertInstitutionDialog = (props: UpsertInstitutionDialogProps) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [currentState, setCurrentState] = useState(getInstitutionTemplate());

  useEffect(() => {
    setCurrentState(
      props.clickedInstitution
        ? cloneDeep(props.clickedInstitution)
        : getInstitutionTemplate(),
    );
  }, [props.clickedInstitution, props.open]);

  const handleConfirm = () => {
    setLoading(true);

    upsertInstitution(currentState)
      .then(() => {
        toast.success(
          props.clickedInstitution
            ? t('institution.successful.update')
            : t('institution.successful.add'),
        );

        props.onModify();
      })
      .catch((err) => toastApiError(err))
      .finally(() => setLoading(false));
  };

  const handleChangeName = (e: any) => {
    setCurrentState({ ...currentState, name: e.target.value });
  };

  const handleChangeCity = (newCity: string) => {
    setCurrentState({ ...currentState, city: newCity });
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      aria-labelledby='institution-dialog-title'
      PaperProps={{
        sx: {
          m: { xs: 2, lg: 4 },
          width: { xs: 'calc(100% - 32px)', lg: 'auto' },
          maxHeight: { xs: 'calc(100% - 32px)', lg: 'calc(100% - 64px)' },
        },
      }}
    >
      <Waiting open={loading} />
      <DialogTitle
        id='institution-dialog-title'
        sx={{
          ...sharedStyles.h6,
          color: sharedColors.gray6,
          px: { xs: 2, lg: 3 },
        }}
      >
        {props.clickedInstitution
          ? t('institution.dialog.update')
          : t('institution.dialog.add')}
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          width: { xs: 'auto', lg: 500 },
          px: { xs: 2, lg: 3 },
        }}
      >
        <TextField
          label={t('institution.name')}
          value={currentState.name}
          onChange={handleChangeName}
          size='small'
          error={isEmpty(currentState.name)}
          sx={{ mt: 1.5 }}
          fullWidth
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              lg: '250px minmax(0, 1fr)',
            },
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <CityDropdown
            label={t('institution.city') as string}
            required
            value={currentState.city}
            onChange={handleChangeCity}
          />

          <NumberInput
            label={t('institution.manager_approval_count') as string}
            value={currentState.managerApprovalCount}
            onChange={(x) =>
              setCurrentState({ ...currentState, managerApprovalCount: x })
            }
            error={currentState.managerApprovalCount < 0}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, lg: 2.5 }, pb: 1.5 }}>
        <Button
          variant='contained'
          color='inherit'
          onClick={props.onClose}
          sx={sharedStyles.buttonText}
        >
          {t('shared.cancel')}
        </Button>
        <Button
          variant='contained'
          color='primary'
          disabled={
            isEmpty(currentState.name) || currentState.managerApprovalCount < 0
          }
          onClick={handleConfirm}
          sx={sharedStyles.buttonText}
        >
          {props.clickedInstitution
            ? t('institution.dialog.update')
            : t('institution.dialog.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpsertInstitutionDialog;
